import {useEffect} from 'react'

import {sessionManager} from '../utils'

/**
 * Longest gap between two `sessionManager.reset()` calls driven by local
 * interaction. Small relative to the 30-minute idle window, so the reset is
 * effectively continuous while the admin is working, but coarse enough that a
 * burst of thousands of pointer events during a large photo selection collapses
 * to a handful of calls.
 */
export const SESSION_ACTIVITY_THROTTLE_MS = 30 * 1000

/**
 * Counts local interaction as session activity (AB#24880).
 *
 * The idle-session timer is otherwise reset only by an axios response (see
 * `services/api/http.ts`), so any screen the user works on without talking to
 * the API looks idle. The photo uploader is the worst case: selecting up to
 * 2,000 files in the Filestack picker makes no requests at all, so an admin
 * could be actively working and still be expired mid-selection.
 *
 * Mounted once, app-wide, next to `sessionManager.init` in `app/Router.tsx`.
 */
export const useSessionActivity = (): void => {
  useEffect(() => {
    let lastResetAt = 0

    const registerActivity = (): void => {
      const now = Date.now()
      if (now - lastResetAt < SESSION_ACTIVITY_THROTTLE_MS) {
        return
      }
      lastResetAt = now
      sessionManager.reset()
    }

    // `pointerdown` covers mouse, touch and pen in one listener; `keydown`
    // covers keyboard-only work (and the picker's own keyboard navigation).
    // Both are captured on `window` so they see the events regardless of which
    // subtree — including Filestack's injected DOM — is focused.
    window.addEventListener('pointerdown', registerActivity)
    window.addEventListener('keydown', registerActivity)

    return (): void => {
      window.removeEventListener('pointerdown', registerActivity)
      window.removeEventListener('keydown', registerActivity)
    }
  }, [])
}
